import { NavLink } from 'react-router-dom';
import { useState, useEffect } from 'react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: '📊' },
  { path: '/agents', label: 'Agents', icon: '🤖' },
  { path: '/projects', label: 'Projects', icon: '📁' },
  { path: '/swarm', label: 'Swarm', icon: '🐝' },
  { path: '/templates', label: 'Templates', icon: '📋' },
  { path: '/knowledge', label: 'Knowledge', icon: '📚' },
  { path: '/integrations', label: 'Integrations', icon: '🔗' },
  { path: '/webhooks', label: 'Webhooks', icon: '🔔' },
  { path: '/share', label: 'Sharing', icon: '👥' },
  { path: '/api-keys', label: 'API Keys', icon: '🔑' },
  { path: '/analytics', label: 'Analytics', icon: '📈' },
  { path: '/pricing', label: 'Pricing', icon: '💰' },
  { path: '/billing', label: 'Billing', icon: '💳' },
  { path: '/settings', label: 'Settings', icon: '⚙️' },
];

export default function Sidebar() {
  const [open, setOpen] = useState(false);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open]);

  const handleNavClick = () => {
    setOpen(false);
  };

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-gray-900 rounded-lg text-white shadow-lg hover:bg-gray-800 transition-colors"
        aria-label="Open navigation menu"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
        </svg>
      </button>

      {/* Mobile overlay */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/50 transition-opacity"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        role="complementary"
        aria-label="Sidebar"
        className={`
          fixed inset-y-0 left-0 z-40 w-64 bg-gray-900 text-white p-4 transform transition-transform duration-300 ease-in-out
          md:static md:translate-x-0 md:min-h-screen md:transition-none
          ${open ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-purple-400">InsightSwarm</h1>
            <p className="text-xs text-gray-500">AI Agent Platform</p>
          </div>
          {/* Mobile close button */}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="md:hidden p-1.5 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
            aria-label="Close navigation menu"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav role="navigation" aria-label="Main navigation" className="space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`
              }
              end={item.path === '/'}
            >
              <span aria-hidden="true">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  );
}
